import { useState, useRef, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { chatStoreInstance } from "../stores";
import { DEFAULT_CHAT_TITLE, LAST_CHAT_ID_KEY } from "../constants";
import { ChatInputRef } from "../ChatInput";
import { StreamEventType } from "../types";

import { ENDPOINTS } from "@/api/application-api";
import {
  useRecentVibeChatSessions,
  ChatSession as APIChatSession,
  RecentVibeChatSessionsResponse,
} from "@/queries/vibe/useRecentVibeChatSessions";
import {
  useDeleteVibeChatSession,
  useDeleteAllVibeChatSessions,
} from "@/queries/vibe/useDeleteVibeChatSession";
import { useCancelVibeChat } from "@/queries/vibe/useCancelVibeChat";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import { useAuth } from "@/context/auth-context";
import { jsonAuthHeaders } from "@/utils/headers";
import { convertWorkspaceUrlsToTagsAsync } from "@/utils/workspaceUrlTags";
import { handleVibeChatToolResultInvalidations } from "@/components/VibeChat/utils/invalidateSmartTableQueries";

const RECENT_SESSIONS_PAGE = 1;
const RECENT_SESSIONS_PER_PAGE = 8;

const createSendMessageCallbacks = (
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: number,
) => {
  const invalidateFolderEntities = () => {
    queryClient.invalidateQueries({
      queryKey: ["folder-entities"],
    });
  };
  const handleToolResult = (
    payload: Parameters<typeof handleVibeChatToolResultInvalidations>[2],
  ) => {
    handleVibeChatToolResultInvalidations(queryClient, workspaceId, payload);
  };

  return {
    onStreamComplete: invalidateFolderEntities,
    onAgentProgress: invalidateFolderEntities,
    onAgentComplete: invalidateFolderEntities,
    onToolResult: handleToolResult,
  };
};

export { chatStoreInstance };

export const useVibeChat = () => {
  const [inputValue, setInputValue] = useState("");
  const chatInputRef = useRef<ChatInputRef>(null);
  const workspace = useActiveWorkspace();
  const auth = useAuth();
  const queryClient = useQueryClient();

  const chatStore = chatStoreInstance;

  useEffect(() => {
    chatStore.setAuthToken(auth?.userToken || null);
  }, [auth?.userToken, chatStore]);

  const upsertRecentSession = useCallback(
    (sessionId: string, updates: { name?: string; workspaceId?: number }) => {
      queryClient.setQueriesData<RecentVibeChatSessionsResponse>(
        { queryKey: ["recent-vibe-chat-sessions"] },
        data => {
          if (!data) {
            return data;
          }

          const now = new Date().toISOString();
          const exists = data.vibe_chat_sessions.some(s => s.id === sessionId);
          if (exists) {
            return {
              ...data,
              vibe_chat_sessions: data.vibe_chat_sessions.map(s =>
                s.id === sessionId ? { ...s, name: updates.name ?? s.name, updated_at: now } : s,
              ),
            };
          }

          if (data.page !== RECENT_SESSIONS_PAGE || updates.name !== undefined) {
            return data;
          }

          const newSession: APIChatSession = {
            id: sessionId,
            workspace_id: updates.workspaceId ?? 0,
            created_at: now,
            updated_at: now,
            name: DEFAULT_CHAT_TITLE,
            conversation_history: [],
            created_by: null,
          };
          return {
            ...data,
            vibe_chat_sessions: [newSession, ...data.vibe_chat_sessions],
            total: data.total + 1,
          };
        },
      );
    },
    [queryClient],
  );

  useEffect(
    () =>
      chatStore.addEventListener((eventType, data) => {
        const sessionId = data.session_id as string | undefined;
        if (!sessionId) {
          return;
        }
        if (eventType === StreamEventType.SESSION_CREATED) {
          upsertRecentSession(sessionId, {
            workspaceId: data.workspace_id as number | undefined,
          });
        } else if (eventType === StreamEventType.SESSION_NAME_GENERATED) {
          upsertRecentSession(sessionId, { name: data.name as string });
        }
      }),
    [chatStore, upsertRecentSession],
  );

  const { data: recentSessionsData, isFetching } = useRecentVibeChatSessions({
    page: RECENT_SESSIONS_PAGE,
    per_page: RECENT_SESSIONS_PER_PAGE,
  });

  const [isInitialRestorePending, setIsInitialRestorePending] = useState(
    () => typeof window !== "undefined" && Boolean(sessionStorage.getItem(LAST_CHAT_ID_KEY)),
  );

  const isLoadingChat =
    isInitialRestorePending &&
    chatStore.messages.length === 0 &&
    !chatStore.isLoading &&
    isFetching;
  const deleteSessionMutation = useDeleteVibeChatSession();
  const deleteAllSessionsMutation = useDeleteAllVibeChatSessions();
  const cancelVibeChatMutation = useCancelVibeChat();

  const hasRestoredSessionRef = useRef(false);

  useEffect(() => {
    if (!recentSessionsData) {
      return;
    }

    if (!isFetching) {
      setIsInitialRestorePending(false);
    }

    if (recentSessionsData.vibe_chat_sessions) {
      const sessions = recentSessionsData.vibe_chat_sessions.map(session => ({
        id: session.id,
        title: session.name || "Untitled Chat",
        createdAt: new Date(session.created_at),
      }));

      const apiSessionIds = new Set(sessions.map(s => s.id));
      const existingSessionsMap = new Map(chatStore.chatSessions.map(s => [s.id, s]));

      // Preserve local sessions that aren't in API yet (e.g., new chats being typed)
      const localOnlySessions = chatStore.chatSessions.filter(s => !apiSessionIds.has(s.id));

      const mergedSessions = sessions.map(session => {
        const existing = existingSessionsMap.get(session.id);
        if (existing) {
          const shouldPreserveTitle =
            existing.isGeneratingName ||
            (existing.title !== session.title &&
              existing.title !== DEFAULT_CHAT_TITLE &&
              existing.title !== "Untitled Chat");

          return {
            ...existing,
            title: shouldPreserveTitle ? existing.title : session.title,
            createdAt: session.createdAt,
          };
        }
        return session;
      });

      // Add local-only sessions at the beginning (they're the most recent)
      chatStore.setChatSessions([...localOnlySessions, ...mergedSessions]);

      if (!hasRestoredSessionRef.current && sessions.length > 0) {
        hasRestoredSessionRef.current = true;

        const lastChatId = sessionStorage.getItem(LAST_CHAT_ID_KEY);
        const lastChat = lastChatId ? sessions.find(s => s.id === lastChatId) : null;

        if (lastChat) {
          const sessionData = recentSessionsData.vibe_chat_sessions.find(s => s.id === lastChatId);
          // Only load history if chat doesn't have messages in memory
          // This preserves in-progress messages when restoring a session
          const existingMessages = chatStore.messagesByChatId.get(lastChat.id);
          if (
            sessionData?.conversation_history &&
            (!existingMessages || existingMessages.length === 0)
          ) {
            chatStore.loadChatHistory(lastChat.id, sessionData.conversation_history);
          }
          chatStore.selectChat(lastChat.id);
        }
      }
    }
  }, [recentSessionsData, isFetching, chatStore]);

  useEffect(() => {
    if (chatStore.currentChatId) {
      sessionStorage.setItem(LAST_CHAT_ID_KEY, chatStore.currentChatId);
    }
  }, [chatStore.currentChatId]);

  const authTokenRef = useRef<string | null>(null);
  useEffect(() => {
    authTokenRef.current = auth?.userToken ?? null;
  }, [auth?.userToken]);

  const cancelSentRef = useRef(false);
  useEffect(() => {
    cancelSentRef.current = false;

    const sendCancelOnUnload = () => {
      if (cancelSentRef.current || !authTokenRef.current) {
        return;
      }

      const activeStreamChatIds = chatStore.stream.getActiveStreamChatIds();
      if (activeStreamChatIds.length === 0) {
        return;
      }

      cancelSentRef.current = true;
      for (const chatId of activeStreamChatIds) {
        const body = JSON.stringify({
          session_id: chatId,
          conversation_history: chatStore.getConversationHistoryForChat(chatId),
        });
        fetch(ENDPOINTS.cancel_vibe_chat, {
          method: "POST",
          headers: jsonAuthHeaders(authTokenRef.current),
          body,
          keepalive: true,
        }).catch(() => undefined);
      }
    };

    const handleBeforeUnload = () => {
      sendCancelOnUnload();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [chatStore]);

  const sendMessage = useCallback(
    async (rawMessage: string, options?: { newChat?: boolean }) => {
      if (!workspace?.id) {
        return;
      }

      const message = await convertWorkspaceUrlsToTagsAsync(rawMessage, {
        userToken: auth?.userToken,
      });
      if (!message.trim()) {
        return;
      }

      chatStore.setFollowMode(true);
      const workspaceId =
        typeof workspace.id === "string" ? parseInt(workspace.id, 10) : workspace.id;
      if (options?.newChat) {
        const chatId = chatStore.createNewChat();
        chatStore.selectChat(chatId);
      }
      setInputValue("");
      await chatStore.handleSendMessage(
        message,
        workspaceId,
        createSendMessageCallbacks(queryClient, workspaceId),
      );
    },
    [chatStore, workspace?.id, queryClient, auth?.userToken],
  );

  const handleRetry = useCallback(
    async (messageIndex?: number) => {
      if (chatStore.isLoading || !workspace?.id) {
        return;
      }
      const workspaceId =
        typeof workspace.id === "string" ? parseInt(workspace.id, 10) : workspace.id;
      const message = chatStore.popUserMessageForRetry(messageIndex);
      if (!message) {
        return;
      }
      await chatStore.handleSendMessage(
        message,
        workspaceId,
        createSendMessageCallbacks(queryClient, workspaceId),
      );
    },
    [chatStore, workspace?.id, queryClient],
  );

  const handleChatSelect = useCallback(
    (chat: APIChatSession) => {
      // Only load history if chat doesn't have messages in memory
      // This preserves in-progress messages when switching back to a chat
      const existingMessages = chatStore.messagesByChatId.get(chat.id);
      chatStore.selectChat(chat.id);
      setInputValue("");
      if (chat.conversation_history && (!existingMessages || existingMessages.length === 0)) {
        requestAnimationFrame(() => {
          chatStore.loadChatHistory(chat.id, chat.conversation_history);
        });
      }
    },
    [chatStore],
  );

  const handleNewChat = useCallback(() => {
    setIsInitialRestorePending(false);
    const chatId = chatStore.createNewChat();
    chatStore.selectChat(chatId);
    setInputValue("");
    requestAnimationFrame(() => {
      chatInputRef.current?.focus();
    });
  }, [chatStore]);

  const handleStop = useCallback(async () => {
    if (chatStore.currentChatId) {
      await cancelVibeChatMutation.mutateAsync({
        sessionId: chatStore.currentChatId,
        conversationHistory: chatStore.currentConversationHistory,
      });
      queryClient.invalidateQueries({ queryKey: ["recent-vibe-chat-sessions"] });
    }
    chatStore.abortRequest();
  }, [chatStore, cancelVibeChatMutation, queryClient]);

  const handleDeleteChat = useCallback(
    async (chatId: string) => {
      await deleteSessionMutation.mutateAsync(chatId);
      chatStore.removeSession(chatId);
      if (chatStore.currentChatId === chatId) {
        chatStore.createNewChat();
      }
    },
    [deleteSessionMutation, chatStore],
  );

  const handleDeleteAllChats = useCallback(async () => {
    await deleteAllSessionsMutation.mutateAsync();
    chatStore.clearAllSessions();
    chatStore.createNewChat();
  }, [deleteAllSessionsMutation, chatStore]);

  return {
    chatStore,
    inputValue,
    setInputValue,
    chatInputRef,
    workspace,
    isLoadingChat,
    sendMessage,
    handleRetry,
    handleStop,
    handleChatSelect,
    handleNewChat,
    handleDeleteChat,
    handleDeleteAllChats,
  };
};
